import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
  DashboardModulePresentation,
  DashboardPanelDefinition,
  DashboardPanelResponse,
} from "../types";
import { dashboardPanelErrorFrom, useOrganizationDashboardsApi } from "../api";
import { useDashboardContext } from "../DashboardContext";
import { getDashboardPresentation } from "../presentationRegistry";
import { DrilldownDialog } from "./DrilldownDialog";

interface Props {
  organizationId: string;
  moduleId: string;
  level: "overview" | "detail";
  panel: DashboardPanelDefinition;
}
export const DashboardPanel: React.FC<Props> = ({
  organizationId,
  moduleId,
  level,
  panel,
}) => {
  const { getPanel } = useOrganizationDashboardsApi();
  const { range, refreshVersion, recordPanelUpdate } = useDashboardContext();
  const [response, setResponse] = useState<DashboardPanelResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getPanel(
        organizationId,
        moduleId,
        panel.panel_id,
        range,
      );
      setResponse(result);
      if (result.updated_at) recordPanelUpdate(result.updated_at);
    } catch (reason) {
      const namedError = dashboardPanelErrorFrom(reason);
      setError(
        namedError?.message ??
          (reason instanceof Error
            ? reason.message
            : "Dashboard data could not be loaded."),
      );
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [
    getPanel,
    organizationId,
    moduleId,
    panel.panel_id,
    range,
    recordPanelUpdate,
  ]);
  useEffect(() => {
    void load();
  }, [load, refreshVersion]);
  useEffect(() => {
    const seconds = panel.refresh_interval_seconds;
    if (!seconds || seconds < 1) return;
    const timer = window.setInterval(() => void load(), seconds * 1000);
    return () => window.clearInterval(timer);
  }, [load, panel.refresh_interval_seconds]);
  const presentation: DashboardModulePresentation =
    getDashboardPresentation(moduleId);
  const render =
    level === "overview"
      ? presentation.render_overview
      : presentation.render_detail;
  const content = render?.(panel, response?.data) ?? (
    <Typography level="body-sm" color="neutral">
      No renderer is registered for this panel.
    </Typography>
  );
  const panelData =
    response?.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
      ? (response.data as Record<string, unknown>)
      : {};
  const excludedUnattributedRecords =
    typeof panelData.excluded_unattributed_records === "number"
      ? panelData.excluded_unattributed_records
      : 0;
  return (
    <>
      <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent sx={{ gap: 1.25 }}>
          <Stack
            direction="row"
            alignItems="start"
            justifyContent="space-between"
            gap={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography level="title-md">{panel.title}</Typography>
              {panel.description && (
                <Typography level="body-xs" color="neutral">
                  {panel.description}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {response?.status && response.status !== "ok" && (
                <Chip
                  size="sm"
                  variant="soft"
                  color={response.status === "error" ? "danger" : "neutral"}
                >
                  {response.status}
                </Chip>
              )}
              {panel.drilldown && (
                <Button
                  size="sm"
                  variant="plain"
                  aria-label={`Expand ${panel.title}`}
                  onClick={() => setDrilldownOpen(true)}
                >
                  <OpenInFullIcon />
                </Button>
              )}
            </Stack>
          </Stack>
          {!loading && response?.updated_at && (
            <Typography level="body-xs" color="neutral">
              Data updated {new Date(response.updated_at).toLocaleString()}
            </Typography>
          )}
          {loading ? (
            <Box role="status" aria-label={`Loading ${panel.title}`}>
              <Skeleton variant="rectangular" height={110} />
            </Box>
          ) : error ? (
            <Alert color="danger" role="alert">
              <Stack spacing={1}>
                <Typography level="title-sm">
                  Could not load {panel.title}
                </Typography>
                <Typography level="body-sm">{error}</Typography>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => void load()}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          ) : response?.status === "error" ? (
            <Alert color="danger" role="alert">
              <Stack spacing={0.5}>
                <Typography level="title-sm">
                  {response.error?.message ??
                    "This dashboard panel could not be loaded."}
                </Typography>
                {response.error?.code && (
                  <Typography level="body-xs">
                    Error: {response.error.code}
                  </Typography>
                )}
                {response.error?.correlation_id && (
                  <Typography level="body-xs">
                    Reference: {response.error.correlation_id}
                  </Typography>
                )}
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => void load()}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          ) : response?.status === "unavailable" ? (
            <Alert color="neutral" role="status">
              {response.unavailable_reason ||
                "This data is not available for the selected period."}
            </Alert>
          ) : response?.status === "empty" ? (
            <Typography color="neutral" role="status">
              No data for this period.
            </Typography>
          ) : (
            <Box>{content}</Box>
          )}
          {!loading && !error && excludedUnattributedRecords > 0 && (
            <Typography level="body-xs" color="neutral" role="status">
              {excludedUnattributedRecords} records excluded (no workspace
              attribution)
            </Typography>
          )}
        </CardContent>
      </Card>
      <DrilldownDialog
        open={drilldownOpen}
        onClose={() => setDrilldownOpen(false)}
        organizationId={organizationId}
        moduleId={moduleId}
        panelId={panel.panel_id}
        title={panel.title}
        definition={panel.drilldown}
      />
    </>
  );
};
